import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Toggle from '@cloudscape-design/components/toggle';
import { AdminRole } from '@etiquetachef/types';
import { setRegionsConfigCallable } from '@/lib/callables';
import { subscribeRegionsConfig, type RegionsConfigResult } from '@/lib/firestoreTenantDetail';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { useAdminRole } from '@/auth/useAdminRole';

/**
 * Teto do motivo — o mesmo `max(500)` de `SetRegionsConfigInput`. Auditoria 2026-10-29 10:57 +0100
 * (#1078): acima dele o `input.parse` do `callable()` lançava `ZodError` e o confirm mostrava o JSON
 * cru do erro. Cortar na digitação nunca deixa o envio passar do limite.
 */
const REASON_MAX = 500;

/** `details.reason` do `failed-precondition` do `setRegionsConfig` quando a região não está habilitada nas Functions (era o caso antes da F4, #1080). */
const REGION_NOT_ENABLED_REASON = 'region_not_enabled';

/** Lê `details.reason` de um erro de callable (`FirebaseError` de `firebase/functions`). */
function errorReason(err: unknown): string | null {
  const details = (err as { details?: unknown } | null)?.details;
  if (details !== null && typeof details === 'object' && 'reason' in details) {
    const reason = (details as { reason: unknown }).reason;
    return typeof reason === 'string' ? reason : null;
  }
  return null;
}

/**
 * Chave `config/regions.europeWest1Enabled` na aba Países: se tenants NOVOS de fora da América do
 * Sul nascem na Bélgica (#960).
 *
 * Origem: #1078 — 2026-10-29 10:20 +0100 (F2 da #960).
 *
 * Por quê: o mapa país → região (coluna Região) pode ser preparado antes da Bélgica existir; esta
 * chave o põe em vigor e é o rollback de toda a #960. Desligada (ou doc ausente/inválido), todo
 * tenant novo nasce em São Paulo — o mesmo que o servidor lê em `lib/regionsConfig.ts`.
 *
 * Contrato: assina `config/regions` (`subscribeRegionsConfig`). O `Toggle` só fica habilitado para
 * ROOT (`useAdminRole`); mudar abre `AdminConfirm` com motivo obrigatório e chama
 * `setRegionsConfig`. A recusa `region_not_enabled` (região não habilitada nas Functions; não ocorre desde a F4 #1080, mas fica tratada) vira
 * o texto `regionsSwitch.notEnabled` dentro do confirm, que fica aberto (#1021).
 *
 * Usado por: `features/countries/CountriesTab.tsx` (topo da aba Países de `/countries`).
 *
 * Arquitetura: apresentação; quem autoriza é o servidor (`ADMIN_PERMISSIONS.setTenantRegion`,
 * ROOT) e a rule `match /config/regions` (escrita negada ao cliente).
 * Spec: `docs/screens/countries-admin.md`.
 *
 * Issues: #960 (desenho), #1078 (F2), #1080 (F4 — habilita ligar).
 */
export function RegionsSwitch(): React.JSX.Element {
  const { t } = useTranslation();
  const role = useAdminRole();
  const isRoot = role === AdminRole.ROOT;
  const [state, setState] = useState<RegionsConfigResult>({ kind: 'LOADING' });
  // Valor pedido, aguardando confirmação (null = confirm fechado).
  const [pending, setPending] = useState<boolean | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => subscribeRegionsConfig(setState), []);

  const enabled = state.kind === 'READY' && state.config?.europeWest1Enabled === true;

  /** Grava o valor pendente; erro fica dentro do confirm aberto. */
  async function apply(): Promise<void> {
    if (pending === null) return;
    if (reason.trim().length === 0) {
      setError(t('admin.countries.catalog.errors.reasonRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setRegionsConfigCallable({ europeWest1Enabled: pending, reason: reason.trim() });
      toast.success(t('admin.countries.catalog.regionsSwitch.saved'));
      setPending(null);
    } catch (err) {
      setError(
        errorReason(err) === REGION_NOT_ENABLED_REASON
          ? t('admin.countries.catalog.regionsSwitch.notEnabled')
          : err instanceof Error
            ? err.message
            : t('admin.countries.catalog.errorGeneric'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container
      header={
        <Header variant="h2" description={t('admin.countries.catalog.regionsSwitch.description')}>
          {t('admin.countries.catalog.regionsSwitch.title')}
        </Header>
      }
    >
      {state.kind === 'ERROR' ? (
        <Box color="text-status-error">{t('admin.countries.catalog.regionsSwitch.errorLoad')}</Box>
      ) : (
        <SpaceBetween size="xs">
          <Toggle
            checked={enabled}
            disabled={!isRoot || state.kind === 'LOADING'}
            onChange={({ detail }): void => {
              setReason('');
              setError(null);
              setPending(detail.checked);
            }}
          >
            {t('admin.countries.catalog.regionsSwitch.label')}
          </Toggle>
          {role !== undefined && !isRoot && (
            <Box color="text-status-inactive" fontSize="body-s">
              {t('admin.countries.catalog.regionsSwitch.rootOnly')}
            </Box>
          )}
        </SpaceBetween>
      )}

      <AdminConfirm
        visible={pending !== null}
        title={
          pending === true
            ? t('admin.countries.catalog.regionsSwitch.enableTitle')
            : t('admin.countries.catalog.regionsSwitch.disableTitle')
        }
        confirmLabel={t('admin.countries.catalog.confirmApply')}
        cancelLabel={t('admin.countries.catalog.cancel')}
        loading={busy}
        onConfirm={(): void => {
          void apply();
        }}
        onDismiss={(): void => {
          setPending(null);
          setError(null);
        }}
      >
        <SpaceBetween size="m">
          <Box variant="p">{t('admin.countries.catalog.regionsSwitch.confirmDescription')}</Box>
          <FormField label={t('admin.countries.catalog.reasonLabel')}>
            <Input
              value={reason}
              onChange={({ detail }): void => { setReason(detail.value.slice(0, REASON_MAX)); }}
              placeholder={t('admin.countries.catalog.reasonPlaceholder')}
            />
          </FormField>
          {error !== null && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </AdminConfirm>
    </Container>
  );
}
